import { restapi } from './restapi';
import {
	type Country,
	type EWUser,
	FullWarning,
	FullWarningEvent,
	type PartialFullWarningEvent,
	Warning,
	WarningEvent,
	type WarningEventComment,
	WarningEventType,
} from './types';


export function loadCountries(partialName: string) {
	const config = {
		params: {
			value: partialName,
		},
	};
	return restapi.get<Country[]>(`country/`, config).then(res => res.data);
}

export function userCanEditEvents(user: EWUser) {
	return user.canEdit || user.isSuperUser;
}

export function getEvent(id: number) {
	return restapi.get<FullWarningEvent>(`event/${id}/`).then(res => res.data);
}

export function isFirstEvent(e: WarningEvent) {
	// Either we are creating the warning or we are editing the "raised" event
	return e.type === WarningEventType.raised;
}

export function getWarning(id: number) {
	return restapi.get<FullWarning>(`warning/${id}/`).then(res => res.data);
}

function saveWarning(warning: Warning) {
	// only main data, no sub-objects are saved here
	const data = {
		...warning,
		raised: undefined,
		closed: undefined,
		latestEvent: undefined,
	};
	return warning.id ?
		restapi.put<FullWarningEvent>(`warning/${warning.id}/`, data).then(res => res.data) :
		restapi.post<FullWarningEvent>(`warning/`, data).then(res => res.data);
}

export function saveEvent(event: FullWarningEvent, changes: PartialFullWarningEvent) {
	console.assert(!!event.warning, 'FullWarningEvent warning must be set for saving', event);
	const warningId = event.warning.id;
	let saveEventPromise!: Promise<{ id: number }>;
	if (isFirstEvent(event)) {
		// handle warning creation and editing with a separate call
		// and (in case of creation) get the warning_id from the response (see below)
		saveEventPromise = saveWarning({...event.warning, ...changes.warning});
	} else {
		// just use a no action promise that return {id: warningId} to simulate async call
		saveEventPromise = new Promise(resolve => {
			setTimeout(() => resolve({id: warningId}), 1);
		});
	}
	const data = {...event, ...changes, warning: undefined, warning_id: warningId};
	return saveEventPromise.then(({id}) => {  // chain two server calls
		data.warning_id = id;  // this may be a newly created warning id
		return event.id ?
			restapi.put<FullWarningEvent>(`event/${event.id}/`, data).then(res => res.data)
			: restapi.post<FullWarningEvent>(`event/`, data).then(res => res.data);
	});
}

export function deleteWarning(warning: Warning) {
	return restapi.delete(`warning/${warning.id}/`);
}

export function deleteEvent(event: FullWarningEvent) {
	return isFirstEvent(event) ?
		deleteWarning(event.warning)  // BE deletes the entire warning and all related events
		: restapi.delete(`event/${event.id}/`);
}

export function approveEvents(ids: number[]) {
	return restapi.post<{}>(`event/approve/`, {ids}).then(res => res.data);
}

export function reopenEvent(event: WarningEvent) {
	return restapi.post<{}>(`event/${event.id}/reopen/`).then(res => res.data);
}

export function saveComment(id: number, eventId: number, message: string, isOpen: boolean) {
	const data = {id: id || undefined, event: eventId, message: message, isOpen: isOpen};
	return id ?
		restapi.put<WarningEventComment>(`comment/${id}/`, data).then(res => res.data)
		: restapi.post<WarningEventComment>(`comment/`, data).then(res => res.data);
}

export function userCanEditEvent(user: EWUser) {
	return user.canEdit || user.isSuperUser;
}

export function userCanComment(user: EWUser) {
	return user.canEdit || user.isSuperUser;
}

export function userCanEditTitle(user: EWUser) {
	return user.isSuperUser;
}
